import React from 'react'
import {
  Box,
  Badge,
  Button,
  Card,
  Container,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack,
  Flex
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { FiArrowRight, FiCheckCircle, FiMessageSquare, FiPieChart, FiSettings, FiTarget, FiEdit3, FiDollarSign } from 'react-icons/fi'
import { SpotlightHeader } from '../components/Spotlight/SpotlightHeader'
import { SpotlightFooter } from '../components/Spotlight/SpotlightFooter'

export const SpotlightHowItWorksPage: React.FC = () => {
  const navigate = useNavigate()

  const handleApply = () => navigate('/for-services/onboarding/basic-setup')
  const handleViewSample = () => navigate('/samples')
  const handleOverview = () => navigate('/')

  return (
    <Box bg="white" minH="100vh" display="flex" flexDirection="column">
      <SpotlightHeader />

      <Box flex={1}>
        {/* Hero */}
        <Box 
          bgGradient="linear(to-br, blue.50, brand.50)" 
          py={{ base: 16, md: 24 }}
          position="relative"
          overflow="hidden"
        >
          {/* Background decoration */}
          <Box
            position="absolute"
            top="-20%"
            right="-10%"
            w="600px"
            h="600px"
            borderRadius="full"
            bg="brand.100"
            opacity={0.3}
            filter="blur(100px)"
          />
          <Box
            position="absolute"
            bottom="-20%"
            left="-10%"
            w="600px"
            h="600px"
            borderRadius="full"
            bg="blue.100"
            opacity={0.3}
            filter="blur(100px)"
          />

          <Container maxW="7xl" position="relative" zIndex={1}>
            <VStack align="center" gap={8} textAlign="center" maxW="4xl" mx="auto">
              <Badge 
                colorPalette="brand" 
                size="lg" 
                px={4} 
                py={2} 
                borderRadius="full"
                variant="subtle"
                border="1px solid"
                borderColor="brand.200"
              >
                The Process
              </Badge>
              <Heading 
                size={{ base: '3xl', md: '4xl', lg: '5xl' }} 
                color="gray.900" 
                lineHeight="shorter"
                fontWeight="extrabold"
                letterSpacing="tight"
              >
                From free signup to clients in <Text as="span" color="brand.600">5 simple steps</Text>—no marketing expertise required
              </Heading>
              <Text fontSize="xl" color="gray.600" maxW="3xl" lineHeight="tall">
                Share your story. We build everything. You approve. Launch when ready. Simple, predictable client acquisition.
              </Text>
              <HStack gap={4} flexWrap="wrap" justify="center">
                <Button 
                  colorPalette="brand" 
                  size="xl" 
                  onClick={handleApply}
                  fontWeight="bold"
                  px={8}
                  h={14}
                  _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
                  transition="all 0.2s"
                >
                  <HStack gap={2}>
                    <Text>Start Getting Clients</Text>
                    <Icon as={FiArrowRight} />
                  </HStack>
                </Button>
                <Button 
                  variant="ghost" 
                  size="xl" 
                  onClick={handleOverview}
                  fontWeight="semibold"
                  color="gray.600"
                  h={14}
                >
                  Back to Overview
                </Button>
              </HStack>
            </VStack>
          </Container>
        </Box>

        {/* Detailed Process */}
        <Container maxW="7xl" py={{ base: 16, md: 24 }}>
          <VStack gap={24} position="relative">
            {/* Connecting Line (Desktop) */}
            <Box 
              display={{ base: 'none', lg: 'block' }}
              position="absolute"
              left="50%"
              top="0"
              bottom="0"
              w="2px"
              bgGradient="linear(to-b, brand.200, brand.500, brand.200)"
              transform="translateX(-50%)"
              zIndex={0}
            />

            {[
              {
                step: 1,
                icon: FiCheckCircle,
                title: "Make a quick intro (60 seconds)",
                subtitle: "No marketing knowledge needed",
                description: "Tell us who you help and the transformation you deliver, then upload a photo.",
                details: ["Free account creation", "Who you help", "Primary outcome", "Photo upload"]
              },
              {
                step: 2,
                icon: FiSettings,
                title: "See your ad instantly",
                subtitle: "We handle the creative — you approve",
                description: "Your story becomes a carousel ad designed to attract your ideal clients. Pick a story type, refine copy, and customize the look.",
                details: ["Instant carousel preview", "3 story types", "Optional story refinements", "Customize colors & layout"]
              },
              {
                step: 3,
                icon: FiEdit3,
                title: "Build your landing page",
                subtitle: "Conversion-ready with clear next steps",
                description: "Edit your hero, offer, testimonials, about, and FAQ. Choose your call-to-action and where leads go. We check conversion readiness before continuing.",
                details: ["CTA: book call or ask question", "Notification emails", "Optional email autoresponder", "Website & social links"]
              },
              {
                step: 4,
                icon: FiTarget,
                title: "Dial in your targeting",
                subtitle: "AI-suggested audience you can edit",
                description: "We generate targeting suggestions based on your profile. Review locations, age, gender, interests and demographics, then save.",
                details: ["AI targeting suggestions", "Locations & radius", "Age & gender", "Interests & demographics"]
              },
              {
                step: 5,
                icon: FiDollarSign,
                title: "Select a pack and activate",
                subtitle: "Pay per pack — no subscriptions",
                description: "Choose a Visibility Pack and checkout. We create your Meta campaign as a paused draft — review if you want, then activate from your dashboard and start receiving inquiries.",
                details: ["Choose a Visibility Pack", "Secure Stripe checkout", "Meta campaign created (paused)", "Activate from dashboard & track results"]
              }
            ].map((item, idx) => (
              <SimpleGrid 
                key={item.step} 
                columns={{ base: 1, lg: 2 }} 
                gap={16} 
                alignItems="center" 
                position="relative"
                zIndex={1}
                w="full"
              >
                {/* Step Number Badge (Center) */}
                <Flex
                  position={{ base: 'relative', lg: 'absolute' }}
                  left={{ base: 0, lg: '50%' }}
                  transform={{ lg: 'translateX(-50%)' }}
                  w={12}
                  h={12}
                  borderRadius="full"
                  bg="brand.600"
                  color="white"
                  align="center"
                  justify="center"
                  fontSize="xl"
                  fontWeight="bold"
                  shadow="lg"
                  mb={{ base: 4, lg: 0 }}
                  border="4px solid white"
                >
                  {item.step}
                </Flex>

                {/* Content Side */}
                <Box order={{ base: 2, lg: idx % 2 === 0 ? 1 : 2 }} textAlign={{ base: 'left', lg: idx % 2 === 0 ? 'right' : 'left' }}>
                  <VStack align={{ base: 'start', lg: idx % 2 === 0 ? 'end' : 'start' }} gap={4}>
                    <HStack gap={3} color="brand.600">
                      <Icon as={item.icon} boxSize={6} />
                      <Text fontWeight="bold" textTransform="uppercase" letterSpacing="wide" fontSize="sm">
                        Step {item.step}
                      </Text>
                    </HStack>
                    <Heading size="2xl" color="gray.900">{item.title}</Heading>
                    <Heading size="md" color="gray.500" fontWeight="medium">{item.subtitle}</Heading>
                    <Text fontSize="lg" color="gray.600" lineHeight="tall">
                      {item.description}
                    </Text>
                  </VStack>
                </Box>

                {/* Visual/Details Side */}
                <Box order={{ base: 3, lg: idx % 2 === 0 ? 2 : 1 }}>
                  <Card.Root 
                    borderWidth="1px" 
                    borderColor="gray.200" 
                    bg="white" 
                    borderRadius="2xl" 
                    shadow="sm"
                    overflow="hidden"
                  >
                    <Card.Body p={8}>
                      <VStack align="start" gap={4}>
                        <Text fontWeight="bold" color="gray.900">What happens:</Text>
                        {item.details.map((detail, i) => (
                          <HStack key={i} gap={3}>
                            <Icon as={FiCheckCircle} color="brand.500" />
                            <Text color="gray.600">{detail}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                </Box>
              </SimpleGrid>
            ))}
          </VStack>
        </Container>

        {/* Transformation Narrative */}
        <Box bg="teal.800" py={{ base: 16, md: 24 }} color="white" position="relative" overflow="hidden">
          <Box
            position="absolute"
            top="-50%"
            right="-20%"
            w="800px"
            h="800px"
            borderRadius="full"
            bgGradient="radial(brand.900, transparent)"
            opacity={0.3}
            filter="blur(100px)"
          />
          <Container maxW="7xl">
            <VStack gap={12} textAlign="center">
              <Badge colorPalette="brand" size="lg" variant="solid">The Transformation</Badge>
              <Heading size={{ base: '2xl', md: '3xl' }} letterSpacing="tight">
                From marketing confusion to client inquiries
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={12} w="full" maxW="4xl" mx="auto">
                <Box bg="whiteAlpha.100" p={8} borderRadius="2xl" border="1px solid" borderColor="whiteAlpha.200">
                  <Heading size="lg" mb={6} color="red.300">Before</Heading>
                  <VStack align="start" gap={3} textAlign="left">
                    <Text color="gray.300">• Struggling to find clients</Text>
                    <Text color="gray.300">• Trying to learn marketing</Text>
                    <Text color="gray.300">• Wasting time on social media</Text>
                    <Text color="gray.300">• Inconsistent income</Text>
                  </VStack>
                </Box>
                <Box bg="brand.900" p={8} borderRadius="2xl" border="1px solid" borderColor="brand.500">
                  <Heading size="lg" mb={6} color="brand.300">After</Heading>
                  <VStack align="start" gap={3} textAlign="left">
                    <Text color="white" fontWeight="medium">• Steady stream of inquiries</Text>
                    <Text color="white" fontWeight="medium">• Booked practice</Text>
                    <Text color="white" fontWeight="medium">• Predictable growth</Text>
                    <Text color="white" fontWeight="medium">• All without becoming a marketer</Text>
                  </VStack>
                </Box>
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        {/* Deep Dive Grid */}
        <Box bg="gray.50" py={{ base: 16, md: 24 }}>
          <Container maxW="7xl">
            <VStack gap={12}>
              <Box textAlign="center" maxW="3xl" mx="auto">
                <Heading size="2xl" color="gray.900" mb={4}>Behind the Scenes</Heading>
                <Text fontSize="xl" color="gray.600">
                  Transparency is key. Here's exactly how we handle your data and content.
                </Text>
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} gap={8}>
                <Card.Root borderWidth="1px" borderColor="gray.200" bg="white" borderRadius="xl" shadow="sm">
                  <Card.Body p={8}>
                    <HStack mb={4} gap={4}>
                      <Box p={3} bg="blue.50" borderRadius="lg" color="blue.600">
                        <Icon as={FiTarget} boxSize={6} />
                      </Box>
                      <Heading size="md" color="gray.900">Getting Started</Heading>
                    </HStack>
                    <VStack align="start" gap={3} color="gray.600">
                      <HStack align="start"><Icon as={FiCheckCircle} color="blue.400" mt={1} /><Text>No credit card required to get started</Text></HStack>
                      <HStack align="start"><Icon as={FiCheckCircle} color="blue.400" mt={1} /><Text>Answer 4 questions about your practice (60-90 seconds)</Text></HStack>
                      <HStack align="start"><Icon as={FiCheckCircle} color="blue.400" mt={1} /><Text>Preview your carousel instantly - you approve before publishing</Text></HStack>
                    </VStack>
                  </Card.Body>
                </Card.Root>

                <Card.Root borderWidth="1px" borderColor="gray.200" bg="white" borderRadius="xl" shadow="sm">
                  <Card.Body p={8}>
                    <HStack mb={4} gap={4}>
                      <Box p={3} bg="green.50" borderRadius="lg" color="green.600">
                        <Icon as={FiPieChart} boxSize={6} />
                      </Box>
                      <Heading size="md" color="gray.900">Privacy & Data</Heading>
                    </HStack>
                    <VStack align="start" gap={3} color="gray.600">
                      <HStack align="start"><Icon as={FiCheckCircle} color="green.400" mt={1} /><Text>All metrics are privacy-safe & aggregated</Text></HStack>
                      <HStack align="start"><Icon as={FiCheckCircle} color="green.400" mt={1} /><Text>No client names or identifying details</Text></HStack>
                      <HStack align="start"><Icon as={FiCheckCircle} color="green.400" mt={1} /><Text>You approve all public data</Text></HStack>
                    </VStack>
                  </Card.Body>
                </Card.Root>

                <Card.Root borderWidth="1px" borderColor="gray.200" bg="white" borderRadius="xl" shadow="sm">
                  <Card.Body p={8}>
                    <HStack mb={4} gap={4}>
                      <Box p={3} bg="purple.50" borderRadius="lg" color="purple.600">
                        <Icon as={FiEdit3} boxSize={6} />
                      </Box>
                      <Heading size="md" color="gray.900">Content Control</Heading>
                    </HStack>
                    <VStack align="start" gap={3} color="gray.600">
                      <HStack align="start"><Icon as={FiCheckCircle} color="purple.400" mt={1} /><Text>You review carousel drafts before publishing</Text></HStack>
                      <HStack align="start"><Icon as={FiCheckCircle} color="purple.400" mt={1} /><Text>Choose from 3 story types (Transformation Story, Qualification First, Practitioner Origin)</Text></HStack>
                      <HStack align="start"><Icon as={FiCheckCircle} color="purple.400" mt={1} /><Text>Update your profile anytime</Text></HStack>
                    </VStack>
                  </Card.Body>
                </Card.Root>
                
                <Card.Root borderWidth="1px" borderColor="gray.200" bg="white" borderRadius="xl" shadow="sm">
                  <Card.Body p={8}>
                    <HStack mb={4} gap={4}>
                      <Box p={3} bg="orange.50" borderRadius="lg" color="orange.600">
                        <Icon as={FiArrowRight} boxSize={6} />
                      </Box>
                      <Heading size="md" color="gray.900">Promotion Guide</Heading>
                    </HStack>
                    <VStack align="start" gap={3} color="gray.600">
                      <HStack align="start"><Icon as={FiCheckCircle} color="orange.400" mt={1} /><Text>Share on LinkedIn & Social Media</Text></HStack>
                      <HStack align="start"><Icon as={FiCheckCircle} color="orange.400" mt={1} /><Text>Add to email signature</Text></HStack>
                      <HStack align="start"><Icon as={FiCheckCircle} color="orange.400" mt={1} /><Text>Embed on your website</Text></HStack>
                    </VStack>
                  </Card.Body>
                </Card.Root>
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        {/* CTA Footer */}
        <Box bg="teal.700" color="white" py={{ base: 20, md: 32 }} position="relative" overflow="hidden">
           <Box
            position="absolute"
            top="0"
            left="0"
            w="full"
            h="full"
            bgGradient="linear(to-br, teal.700, teal.800)"
            opacity={0.9}
            zIndex={0}
          />
          <Container maxW="4xl" position="relative" zIndex={1}>
            <VStack gap={8} textAlign="center">
              <Heading size={{ base: '3xl', md: '4xl' }} letterSpacing="tight">
                Ready to get started?
              </Heading>
              <Text fontSize="xl" color="teal.100" maxW="2xl" lineHeight="tall">
                Pay only for visibility packs when you're ready to grow.
              </Text>
              <Button
                size="xl"
                bg="white"
                color="teal.700"
                fontWeight="bold"
                px={12}
                py={8}
                fontSize="xl"
                onClick={handleApply}
                _hover={{ transform: 'scale(1.05)', shadow: '2xl', bg: 'gray.100' }}
                transition="all 0.2s"
              >
                <HStack gap={3}>
                  <Text>Start Getting Clients</Text>
                  <Icon as={FiArrowRight} />
                </HStack>
              </Button>
            </VStack>
          </Container>
        </Box>
      </Box>

      {/* Mobile Sticky CTA */}
      <Box 
        position="fixed" 
        bottom={0} 
        left={0} 
        right={0} 
        bg="white" 
        p={4} 
        borderTopWidth="1px" 
        borderColor="gray.200"
        shadow="xl" 
        display={{ base: 'block', md: 'none' }}
        zIndex={1000}
      >
        <Button 
          w="full" 
          colorPalette="brand" 
          size="lg" 
          onClick={handleApply}
          fontWeight="bold"
        >
          <HStack gap={2}>
            <Text>Start Getting Clients</Text>
            <Icon as={FiArrowRight} />
          </HStack>
        </Button>
      </Box>

      <SpotlightFooter />
    </Box>
  )
}

export default SpotlightHowItWorksPage
